import axios from "axios";
import ExpiryMap from "expiry-map";
import pMemoize from "p-memoize";
import LocalStorageManager from "../util/LocalStorageManager";

const baseURL = (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') ? (
    // Development will always refer to the .env file (REACT_APP_API_URI)
    process.env.REACT_APP_API_URI
) : (
    // Production always uses API in the same origin
    window.location.origin.toString()
)

// Main Axios instance used to make API calls
const HttpService = axios.create({
    baseURL,
    maxRedirects: 0,
    withCredentials: true,
    headers: {
        "X-Requested-With": "XMLHttpRequest"
    },
});

const memoizeOptions = {
    // maxAge: 10000
    cache: new ExpiryMap(10000)
}

// Flag to track if we're currently getting an app token
let isGettingAppToken = false;
// Queue for callbacks waiting for the app token
const appTokenCallbacks = [];

const getAppToken = async () => {
    // If we're already in the process of getting a token, wait for that to complete
    if (isGettingAppToken) {
        return new Promise((resolve, reject) => {
            appTokenCallbacks.push({ resolve, reject });
        });
    }

    // Set the flag immediately to prevent race condition.
    isGettingAppToken = true;
    
    // Now that we've locked other requests, check for a refreshable token.
    const existingToken = LocalStorageManager.getAuthToken();
    const refreshUntil = LocalStorageManager.getAuthTokenRefreshUntil();
    const now = new Date().getTime();
    if (existingToken && now < refreshUntil) {
        // A refreshable token exists. Let the interceptor handle it.
        // Release the lock and return.
        isGettingAppToken = false;
        return existingToken;
    }
    
    try {
        console.log("Getting new app token");
        const response = await axios.post(
            `${baseURL}/api/login/app`,
            {
                secret: process.env.REACT_APP_API_SECRET
            },
            {
                withCredentials: true,
                headers: {
                    "X-Requested-With": "XMLHttpRequest"
                }
            }
        );
        
        const { token, expires_at, refresh_until } = response.data;
    
        LocalStorageManager.setAuthToken(token);
        LocalStorageManager.setAuthTokenExpiry(new Date(expires_at).getTime());
        LocalStorageManager.setAuthTokenRefreshUntil(new Date(refresh_until).getTime());

        isGettingAppToken = false;
        
        // Resolve all pending callbacks
        appTokenCallbacks.forEach(callback => callback.resolve(token));
        appTokenCallbacks.length = 0;

        return token;
    } catch (error) {
        console.log("Failed to get app token:", error);
        LocalStorageManager.clearLoggedInUserAndToken();
        
        isGettingAppToken = false;
        
        // Reject all pending callbacks
        appTokenCallbacks.forEach(callback => callback.reject(error));
        appTokenCallbacks.length = 0;
        
        throw error;
    }
};

// Flag to track if we're currently refreshing a token
let isRefreshingToken = false;
// Queue for callbacks waiting for the token refresh
const refreshTokenCallbacks = [];

export const refreshToken = async () => {
    // If we're already in the process of refreshing a token, wait for that to complete
    if (isRefreshingToken) {
        return new Promise((resolve, reject) => {
            refreshTokenCallbacks.push({ resolve, reject });
        });
    }
    
    isRefreshingToken = true;
    
    try {
        console.log("Refreshing auth token");

        const response = await axios.post(
            `${baseURL}/api/login/refresh`,
            {},
            {
                withCredentials: true,
                headers: {
                    "X-Requested-With": "XMLHttpRequest"
                }
            }
        );
    
        const { token, expires_at, refresh_until } = response.data;
    
        LocalStorageManager.setAuthToken(token);
        LocalStorageManager.setAuthTokenExpiry(new Date(expires_at).getTime());
        LocalStorageManager.setAuthTokenRefreshUntil(new Date(refresh_until).getTime());
        
        isRefreshingToken = false;
        
        // Resolve all pending callbacks
        refreshTokenCallbacks.forEach(callback => callback.resolve(token));
        refreshTokenCallbacks.length = 0;

        return token;
    } catch (error) {
        console.log("Failed to refresh token:", error);
        LocalStorageManager.clearLoggedInUserAndToken();
        
        isRefreshingToken = false;
        
        // Reject all pending callbacks
        refreshTokenCallbacks.forEach(callback => callback.reject(error));
        refreshTokenCallbacks.length = 0;
        
        // When refresh token fails, we should not try to get a new app token
        // Instead, throw the error to let the caller handle authentication failure
        throw error;
    }
};

// Request interceptor
HttpService.interceptors.request.use(
    async (config) => {
        try {
            let accessToken = LocalStorageManager.getAuthToken();
            const expiresAt = LocalStorageManager.getAuthTokenExpiry();
            const refreshUntil = LocalStorageManager.getAuthTokenRefreshUntil();
            const now = new Date().getTime();

            // 1. If token exists and is expired, try to refresh it
            if (accessToken && expiresAt && now > expiresAt) {
                // Check if the token is still within the refresh window
                if (refreshUntil && now < refreshUntil) {
                    // The `refreshToken` function will throw an error on failure,
                    // which will be caught by the top-level `catch(error)` block.
                    // It already handles clearing the token from storage.
                    console.log("Token expired, refreshing in request interceptor...");
                    accessToken = await refreshToken();
                } else {
                    // It's past the refresh window, so we must get a new app token
                    console.log("Token past refresh window, getting new app token...");
                    accessToken = await getAppToken();
                }
            } 
            // 2. If no token exists at all, get a new app token
            else if (!accessToken) {
                try {
                    accessToken = await getAppToken();
                } catch (error) {
                    console.error("Failed to get app token in request interceptor:", error);
                    // The response interceptor will handle the resulting error
                }
            }
            
            // 3. Attach the final token to the request headers
            if (accessToken) {
                config.headers = {
                    ...config.headers,
                    Authorization: `Bearer ${accessToken}`,
                };
            }
    
            return config;
        } catch(error) {
            return Promise.reject(error);
        }
    },
    (error) => Promise.reject(error)
);

// Response interceptor
HttpService.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (!axios.isCancel(error)) {
            const isUnauthorized = error?.response?.status === 401;
            
            // If we receive a 401, it means our token is unexpectedly invalid.
            // The request interceptor should have already handled expired tokens.
            // This could be due to server-side revocation or other issues.
            // The best strategy is to clear the bad token and let the app handle the failure.
            if (isUnauthorized) {
                console.error("Received 401 Unauthorized. Clearing stored token.");
                LocalStorageManager.clearLoggedInUserAndToken();
            }
        }

        return Promise.reject(error);
    }
);

export { getAppToken }; // Only export getAppToken here, as refreshToken is already exported above
export default HttpService;